import { useCallback, useEffect, useRef } from 'react';

import { useNavigate, useParams } from '@tanstack/react-router';

import type {
  InventoryFormSchema,
  InventoryQuerySchema,
  InventoryUpdateSchema,
} from '@/_pages/settings/inventory-kits/shared/schemas/inventory';
import type { OnPartialUpdate, OnUpdate } from '@/_schemas/types';
import { resetFormState, useEdit, useInvalidate, usePostAuditLog, useProfileCrudActions } from '@/_shared';

import { useRevisionHistory } from './use-revision-history';
import { useSubInventoryItems } from './use-sub-inventory-items';

import { invalidateInventoryQuery, useDeleteMutation, useGetInventory, useUpdateMutation } from '../apis';
import { useFormInputMultipleSKUs } from '../components';
import { inventoryFormToUpdate, inventoryQueryToForm } from '../converters';
import { useFormDefinition } from '../form-context';
import { useVendorDetails } from '../vendor-details';

export function usePage() {
  const hasVendorDetailsSynced = useRef(false);
  const nav = useNavigate();
  const edit = useEdit();
  const form = useFormDefinition();
  const { id } = useParams({ strict: false });
  const { isSKUsPending, handleSKUs } = useFormInputMultipleSKUs();
  const { isVendorDetailsPending, handleVendorDetails } = useVendorDetails(id ?? '');
  const { setHasToInvalidate, removeQuery } = useInvalidate(['INVENTORY', id ?? '']);
  usePostAuditLog({ endpoint: 'inventories', id: `${id}` });

  const inventory = useGetInventory(id ?? '');
  const updateMutation = useUpdateMutation(id ?? '');
  const { isPending: formIsPending } = updateMutation;
  const deleteMutation = useDeleteMutation();
  const { updateSubInventories, isSubInventoriesMutationPending } = useSubInventoryItems();

  const isPending = isVendorDetailsPending || isSKUsPending || formIsPending;

  const getDirtyVendorDetailsIndices = useCallback(() => {
    const { vendor_details } = form.formState.dirtyFields;
    let vendorDetailsIndices: number[] = [];
    if (typeof vendor_details !== 'undefined') {
      vendorDetailsIndices = Object.keys(vendor_details).map((i) => parseInt(i));
    }
    return vendorDetailsIndices;
  }, [form.formState.dirtyFields]);

  const handleSuccessSideEffects = useCallback(() => {
    removeQuery(['INVENTORIES']);
    setHasToInvalidate.setTrue();
  }, [removeQuery, setHasToInvalidate]);

  const {
    onUpdate: onUpdateCrud,
    onPartialUpdate: onPartialUpdateCrud,
    onDelete,
    isDeleting,
    isSuccess,
  } = useProfileCrudActions<InventoryFormSchema, InventoryUpdateSchema, { data: InventoryQuerySchema }>({
    form,
    edit,
    formToMutation: inventoryFormToUpdate,
    updateMutation,
    deleteMutation,
    deleteVariables: { id: `${id}` },
    updateSuccessMessage: 'Inventory has been updated.',
    deleteSuccessMessage: 'Inventory has been deleted.',
    onSuccessSideEffects: handleSuccessSideEffects,
    onDeleteSuccess: () => {
      nav({ to: '/inventory' });
    },
  });

  const onUpdate: OnUpdate<InventoryUpdateSchema, { data: InventoryQuerySchema }> = useCallback(
    async (v, msg = 'Inventory has been updated.', cancelEdit = false, shouldResetForm = false) => {
      return onUpdateCrud(v, msg, cancelEdit, shouldResetForm);
    },
    [onUpdateCrud]
  );

  const onPartialUpdate: OnPartialUpdate<InventoryUpdateSchema> = useCallback(
    async (partialValues, msg, cancelEdit = false, shouldResetForm = false) => {
      return onPartialUpdateCrud(partialValues, msg, cancelEdit, shouldResetForm);
    },
    [onPartialUpdateCrud]
  );

  const onSubmit = useCallback(
    async (data: InventoryFormSchema) => {
      const handledSKUs = await handleSKUs(data.secondary_skus);
      if (handledSKUs === false) {
        return;
      }

      const { result: handledVendorDetails, isRefreshRequired } = await handleVendorDetails(
        data.vendor_details,
        data.vendors,
        getDirtyVendorDetailsIndices()
      );

      if (typeof handledVendorDetails === 'boolean') {
        return;
      }

      await updateSubInventories({
        inventoryId: data.id,
        isChildrenDirty: !!form.formState.dirtyFields?.children,
        newChildren: data.children,
        existingChildren: inventory.data?.data.children,
        parentId: data.parent?.id,
      });

      await onUpdate(
        inventoryFormToUpdate({ ...data, secondary_skus: handledSKUs, vendor_details: handledVendorDetails }),
        'Inventory has been updated.',
        true,
        true
      );

      if (isRefreshRequired) {
        invalidateInventoryQuery(id ?? '');
      }
    },
    [
      handleSKUs,
      handleVendorDetails,
      getDirtyVendorDetailsIndices,
      updateSubInventories,
      form.formState.dirtyFields?.children,
      inventory.data?.data.children,
      onUpdate,
      id,
    ]
  );

  const revisionHistory = useRevisionHistory(form, onUpdate);

  useEffect(() => {
    const { data: response } = inventory;
    if (response) {
      resetFormState(form, inventoryQueryToForm(response.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventory.data]);

  // !important - FOR PRODUCTION WE NEED TO SYNC VENDOR DETAILS for the first time
  useEffect(() => {
    if (hasVendorDetailsSynced.current) {
      return;
    }
    const { data: response, isSuccess } = inventory;
    if (isSuccess) {
      const formData = inventoryQueryToForm(response.data);
      handleVendorDetails(formData.vendor_details, formData.vendors, []).then((res) => {
        if (res.isRefreshRequired) {
          hasVendorDetailsSynced.current = true;
          invalidateInventoryQuery(id ?? '');
        }
      });
    }
    // !important
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventory.isSuccess]);

  console.log(form.formState.errors);

  return {
    id,
    form,
    isPending: isPending || isDeleting || isSubInventoriesMutationPending,
    isSuccess,
    edit,
    isDeleting,
    isReverting: inventory.isFetching || isDeleting || isPending,
    revisionHistory,
    onSubmit,
    onDelete,
    onPartialUpdate,
  };
}

export type UsePageType = ReturnType<typeof usePage>;
